import React, { useEffect, useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import Typography from '@mui/material/Typography';
import { OptionTypeForMedications } from 'components/medicalHistory/types/types';
import MuiAutoComplete from 'components/common/autoComplete';
import styles from 'components/common/common.module.scss';
import AddItemButton from 'components/common/addItemButton';
import clsx from 'clsx';
import { removeItemFromList } from 'components/medicalHistory/helpers';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullableCommunique } from 'types/types';
import useDelay from 'hooks/useDelay';
import { ERxMedicationSearchResultDTO } from 'artifacts/dtos';
import { useERxMedicationSearch } from 'hooks/useApi';

type Props = {
  value: OptionTypeForMedications[];
  id: string;
  placeholder: string;
  onChange?: (item: OptionTypeForMedications[]) => void;
  disabled?: boolean;
};

function SearchableTextBoxMedications({
  placeholder,
  onChange,
  value,
  disabled,
}: Props) {
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;
  const [inputValue, setInputValue] = useState<OptionTypeForMedications[]>(value || []);
  const [inputText, setInputText] = useState('');
  const [data, setData] = useState<ERxMedicationSearchResultDTO[]>([]);
  const delay = useDelay(inputText, 500);
  const {
    mutateAsync: medicationSearch,
    isPending: isLoading,
    isError,
    reset,
  } = useERxMedicationSearch();
  const isValidInputLength = useMemo(() => (inputText.length > 2), [inputText]);
  const [hideSearchBox, setHideSearchBox] = useState(false);

  useEffect(() => {
    if ((delay ?? '').length > 2) {
      medicationSearch({
        search: delay,
        organizationId: communiqueData?.organizationId ?? undefined,
      })
        .then((items) => setData(items))
        .catch(() => setData([]));
    }
  }, [communiqueData?.organizationId, delay, medicationSearch]);

  useEffect(() => {
    if (value?.length) {
      setHideSearchBox(true);
    }
  }, [value]);

  const removeItem = (item: OptionTypeForMedications) => {
    setInputValue((prev) => {
      const { items, isEmpty } = removeItemFromList(prev, item);
      setHideSearchBox(!isEmpty);
      return items;
    });
  };

  const mappingOptions = useMemo(
    () => data?.map((item) => ({
      ...item,
      value: item.name ?? '',
      label: item.name ?? '',
    })),
    [data],
  );

  useEffect(() => {
    if (onChange) onChange(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (value) setInputValue(value);
  }, [value]);

  const onInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
    setInputText(newInputValue);
    reset?.();
    setData([]);
  };
  // eslint-disable-next-line no-nested-ternary
  const genNoOptionsText = isLoading
    // eslint-disable-next-line no-nested-ternary
    ? 'Loading....' : isError
      ? 'Something went wrong' : isValidInputLength
        ? 'No Options' : 'Please type a medicine name, min characters 3';

  const addButtonClass = clsx((inputValue.length === 0 || hideSearchBox)
    ? 'addButton'
    : 'addButtonDisable');

  return (
    <div>
      {inputValue.map((item) => (
        <div key={item.label}>
          <div className={
            clsx(
              ' flex my-2 justify-between',
              { 'autocomplete-selected-item': !item?.readOnly },
              { 'autocomplete-readOnly-item': item?.readOnly },
            )
            }
          >
            <Typography component="p">{item.value}</Typography>
            {!item?.readOnly && (
            <ClearIcon
              onClick={() => { removeItem(item); }}
              fontSize="small"
            />
            )}
          </div>
        </div>
      ))}
      <MuiAutoComplete
        options={mappingOptions}
        value={inputValue}
        placeholder={placeholder}
        onChange={(event, newInputValue) => {
          if (inputValue.length < newInputValue.length) {
            setInputValue(newInputValue);
          }
        }}
        onInputChange={onInputChange}
        noOptionsText={genNoOptionsText}
        hide={hideSearchBox}
        disabled={disabled}
      />
      <p className={styles.separator} />
      <AddItemButton
        className={addButtonClass}
        label="Add Medication"
        onClick={() => setHideSearchBox(false)}
        disabled={(inputValue.length === 0 && !hideSearchBox) || disabled}
      />
    </div>
  );
}

export default SearchableTextBoxMedications;
